import React, { useRef, useEffect, useState } from "react";
import { NodeViewWrapper, NodeViewProps } from "@tiptap/react";

export const ResizableImageNodeView: React.FC<NodeViewProps> = ({
  node,
  updateAttributes,
  selected,
  deleteNode,
  editor,
  getPos,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [originalDimensions, setOriginalDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [isAltTextPopupOpen, setIsAltTextPopupOpen] = useState(false);
  const [altText, setAltText] = useState(node.attrs.alt || "");

  useEffect(() => {
    if (imgRef.current) {
      const img = imgRef.current;
      if (img.complete) {
        setOriginalDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      } else {
        img.onload = () => {
          setOriginalDimensions({
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
        };
      }
    }
  }, [node.attrs.src]);

  useEffect(() => {
    setAltText(node.attrs.alt || "");
  }, [node.attrs.alt]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selected &&
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        editor
          .chain()
          .focus()
          .setTextSelection(editor.state.doc.content.size)
          .run();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selected, editor]);

  const setFocusAtEnd = () => {
    if (typeof getPos === "function") {
      const endPos = getPos();
      if (endPos !== undefined) {
        editor
          .chain()
          .focus()
          .setTextSelection(endPos + node.nodeSize)
          .run();
      } else {
        editor.chain().focus().run();
      }
    } else {
      editor.chain().focus().run();
    }
  };

  const setFocusAtStart = () => {
    if (typeof getPos === "function") {
      const startPos = getPos();
      if (startPos !== undefined) {
        editor.chain().focus().setTextSelection(startPos).run();
      } else {
        editor.chain().focus().run();
      }
    } else {
      editor.chain().focus().run();
    }
  };

  const handleResize = (
    e: React.MouseEvent,
    corner: "tr" | "tl" | "br" | "bl"
  ) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = wrapperRef.current?.offsetWidth ?? 0;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaX = moveEvent.clientX - startX;
      let newWidth;

      if (corner.includes("r")) {
        newWidth = startWidth + deltaX;
      } else {
        newWidth = startWidth - deltaX;
      }

      updateAttributes({ width: `${Math.max(50, newWidth)}px` });
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      setFocusAtEnd();
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const setSize = (width: string | null) => {
    if (width === null) {
      if (originalDimensions) {
        updateAttributes({ width: `${originalDimensions.width}px` });
      } else {
        updateAttributes({ width: "auto" });
      }
    } else {
      updateAttributes({ width });
    }
    setFocusAtEnd();
  };

  const setSmallSize = () => {
    if (originalDimensions) {
      const smallWidth = Math.min(
        200,
        Math.round(originalDimensions.width * 0.7)
      );

      setSize(`${smallWidth}px`);
    } else {
      setSize("200px");
    }
  };

  const setBestFitSize = () => {
    if (originalDimensions && wrapperRef.current) {
      const container = wrapperRef.current.parentElement;
      if (container) {
        const containerWidth = container.offsetWidth;

        const maxWidth = Math.min(
          containerWidth * 0.8,
          originalDimensions.width
        );
        const bestFitWidth = Math.max(200, Math.round(maxWidth));

        setSize(`${bestFitWidth}px`);
      } else {
        const bestFitWidth = Math.max(
          200,
          Math.round(originalDimensions.width * 0.6)
        );
        setSize(`${bestFitWidth}px`);
      }
    } else {
      setSize("400px");
    }
  };

  const editAltText = () => {
    setIsAltTextPopupOpen(true);
  };

  const handleApplyAltText = () => {
    updateAttributes({ alt: altText });
    setIsAltTextPopupOpen(false);
    setFocusAtEnd();
  };

  const handleCancelAltText = () => {
    setAltText(node.attrs.alt || "");
    setIsAltTextPopupOpen(false);
    setFocusAtEnd();
  };

  const handleWrapperClick = (e: React.MouseEvent) => {
    // Only select the image if clicking directly on the wrapper or image
    if (e.target === wrapperRef.current || e.target === imgRef.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleImageClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <NodeViewWrapper
      ref={wrapperRef}
      className={`resizable-image-container relative block leading-none max-w-full ${
        selected ? "border-2 border-blue-500" : "border-2 border-transparent"
      }`}
      style={{
        width: node.attrs.width || "auto",
        display: "block",
        margin:
          node.attrs.textAlign === "center"
            ? "0 auto"
            : node.attrs.textAlign === "right"
            ? "0 0 0 auto"
            : "0",
        textAlign: node.attrs.textAlign || "left",
        marginLeft:
          node.attrs.indentLevel && node.attrs.indentLevel > 0
            ? `${node.attrs.indentLevel * 1.5}rem`
            : undefined,
        marginTop: "0.5rem",
        marginBottom: "0.5rem",
        cursor: "pointer",
      }}
      data-drag-handle
      onClick={handleWrapperClick}
    >
      {/* Cursor position before image */}
      <div
        className="absolute -top-2 left-0 w-2 h-6 bg-transparent cursor-text z-10"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setFocusAtStart();
        }}
        style={{
          background: "transparent",
          cursor: "text",
        }}
      />

      <img
        ref={imgRef}
        src={node.attrs.src}
        alt={node.attrs.alt}
        className="w-full h-auto"
        style={{
          display: "block",
          margin:
            node.attrs.textAlign === "center"
              ? "0 auto"
              : node.attrs.textAlign === "right"
              ? "0 0 0 auto"
              : "0",
        }}
        onClick={handleImageClick}
      />

      {/* Cursor position after image */}
      <div
        className="absolute -bottom-2 right-0 w-2 h-6 bg-transparent cursor-text z-10"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setFocusAtEnd();
        }}
        style={{
          background: "transparent",
          cursor: "text",
        }}
      />

      {selected && (
        <>
          {!isAltTextPopupOpen && (
            <div
              contentEditable={false}
              className="min-w-max absolute -bottom-8 left-0 flex items-center gap-1 bg-white border border-gray-200 rounded-[4px] shadow-lg px-1 py-2 h-6"
            >
              <button
                onClick={setSmallSize}
                className="bg-none border-none px-1 cursor-pointer text-blue-500 text-xs shrink-0"
              >
                Small
              </button>
              <span className="text-gray-400">|</span>
              <button
                onClick={setBestFitSize}
                className="bg-none border-none px-1 cursor-pointer text-blue-500 text-xs shrink-0"
              >
                Best fit
              </button>
              <span className="text-gray-400">|</span>
              <button
                onClick={() => setSize(null)}
                className="bg-none border-none px-1 cursor-pointer text-blue-500 text-xs shrink-0"
              >
                Original size
              </button>
              <span className="text-gray-400">|</span>
              <button
                onClick={editAltText}
                className="bg-none border-none px-1 cursor-pointer text-blue-500 text-xs shrink-0"
              >
                Edit alt text
              </button>
              <span className="text-gray-400">|</span>
              <button
                onClick={deleteNode}
                className="bg-none border-none px-1 cursor-pointer text-blue-500 text-xs shrink-0"
              >
                Remove
              </button>
            </div>
          )}

          {/* Alt Text Popup */}
          {isAltTextPopupOpen && (
            <div className="absolute z-20 w-80 bg-white border border-gray-200 rounded-[4px] shadow-lg p-3 space-y-3 -bottom-30 left-20">
              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">
                  Alt Text
                </label>
                <input
                  type="text"
                  value={altText}
                  onChange={(e) => setAltText(e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none text-sm font-normal"
                  placeholder="Enter alt text for accessibility"
                  autoFocus
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={handleCancelAltText}
                  className="px-3 py-1 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApplyAltText}
                  className="px-3 py-1 text-sm font-medium text-white bg-primary-blue-dk rounded hover:bg-blue-700"
                >
                  Apply
                </button>
              </div>
            </div>
          )}

          {/* Resize handles */}
          <div
            className="absolute w-3 h-3 border-2 border-white bg-blue-500 rounded-sm -top-1.5 -left-1.5 cursor-nwse-resize"
            onMouseDown={(e) => handleResize(e, "tl")}
          />
          <div
            className="absolute w-3 h-3 border-2 border-white bg-blue-500 rounded-sm -top-1.5 -right-1.5 cursor-nesw-resize"
            onMouseDown={(e) => handleResize(e, "tr")}
          />
          <div
            className="absolute w-3 h-3 border-2 border-white bg-blue-500 rounded-sm -bottom-1.5 -left-1.5 cursor-nesw-resize"
            onMouseDown={(e) => handleResize(e, "bl")}
          />
          <div
            className="absolute w-3 h-3 border-2 border-white bg-blue-500 rounded-sm -bottom-1.5 -right-1.5 cursor-nwse-resize"
            onMouseDown={(e) => handleResize(e, "br")}
          />
        </>
      )}
    </NodeViewWrapper>
  );
};
